// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { useMemo, useState } from "react";
import { useBalanceAdjustmentPreview } from "./useBalanceAdjustmentPreview.js";
import {
  isBalanceAdjustmentReasonValid,
  normalizeBalanceAdjustmentAmount,
} from "@payday/contracts";
import type {
  OpenProjectBalanceAdjustmentPreviewResponse,
} from "../../types/openproject";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  LockKeyhole,
  LockKeyholeOpen,
  PencilLine,
  RotateCcw,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";

import { Button, Modal } from "../ui";

import {
  BALANCE_RESOLUTION_PRESENTATION, balanceResolutionOptions, checkLabels,
  isResolutionAction, isCheckKey, balanceAdjustmentUnitOptions, formatValueInUnit,
  type BalanceCheckKey, type BalanceResolutionEmployee, type ResolutionChoice,
  type BalanceAdjustmentUnit, type CrossCheck,
} from "./balanceResolutionModel.js";
export { BALANCE_RESOLUTION_PRESENTATION, balanceResolutionOptions } from "./balanceResolutionModel.js";
export type { BalanceCheckKey, BalanceResolutionEmployee, ResolutionAction } from "./balanceResolutionModel.js";

const modalActionButtonClass =
  "w-full max-w-full min-w-11 whitespace-normal px-4 text-sm sm:w-auto";

const unitLabel = (unit: BalanceAdjustmentUnit) =>
  unit === "hours" ? "hours" : "days";

export function BalanceResolutionModal({
  employee,
  actorName,
  monthLabel,
  monthClosed,
  timesheetLocked,
  busy,
  displayUnit,
  initialCheckKey,
  onClose,
  onOpenPayslip,
  onCorrectPayslipWorked,
  onDownloadTimesheet,
  onToggleTimesheet,
  onReopen,
  onAccept,
  onAdjust,
  onPreviewAdjust,
}: {
  employee: BalanceResolutionEmployee;
  actorName: string;
  monthLabel: string;
  monthClosed: boolean;
  timesheetLocked: boolean;
  busy: boolean;
  displayUnit: "hours" | "days";
  initialCheckKey?: BalanceCheckKey | null;
  onClose: () => void;
  onOpenPayslip?: () => void;
  onCorrectPayslipWorked?: () => void;
  onDownloadTimesheet: () => Promise<unknown>;
  onToggleTimesheet: (action: "lock" | "unlock") => Promise<unknown>;
  onReopen: (reason: string) => Promise<unknown>;
  onAccept: (
    checkKey: BalanceCheckKey,
    resolutionAction: "accept_exception",
    reason: string,
  ) => Promise<unknown>;
  onAdjust: (
    checkKey: BalanceCheckKey,
    amount: number,
    inputUnit: BalanceAdjustmentUnit,
    reason: string,
    preview: OpenProjectBalanceAdjustmentPreviewResponse,
  ) => Promise<unknown>;
  onPreviewAdjust: (
    checkKey: BalanceCheckKey,
    amount: number,
    inputUnit: BalanceAdjustmentUnit,
  ) => Promise<OpenProjectBalanceAdjustmentPreviewResponse>;
}) {
  const reviewableChecks = useMemo(
    () =>
      Object.entries(employee.crossChecks ?? {})
        .filter(
          (entry): entry is [BalanceCheckKey, CrossCheck] =>
            isCheckKey(entry[0]) &&
            entry[0] !== "overtime" &&
            entry[0] !== "remainingVacation" &&
            Boolean(entry[1]) &&
            (entry[1]?.accepted === true ||
              (entry[1]?.state === "red" && !entry[1]?.accepted)),
        )
        .map(([key, check]) => ({ key, check })),
    [employee.crossChecks],
  );
  const issues = reviewableChecks.filter((issue) => !issue.check.accepted);
  const initialIssue =
    reviewableChecks.find((issue) => issue.key === initialCheckKey) ??
    issues[0] ??
    reviewableChecks[0];
  const [selectedKey, setSelectedKey] = useState<BalanceCheckKey>(
    initialIssue?.key ?? "vacation",
  );
  const selectedCheck =
    reviewableChecks.find((issue) => issue.key === selectedKey)?.check ??
    initialIssue?.check;
  const { actions: availableActions, recommendedAction: serverRecommended } =
    useMemo(
      () => balanceResolutionOptions(selectedCheck?.resolution),
      [selectedCheck?.resolution],
    );
  const recommended: ResolutionChoice | null =
    selectedKey === "workload" ? "inspect_payslip" : serverRecommended;
  const orderedActions: ResolutionChoice[] =
    selectedKey === "workload"
      ? [
          "inspect_payslip",
          "correct_extracted_worked",
          ...availableActions.filter((action) => action === "accept_exception"),
        ]
      : serverRecommended
        ? [
            serverRecommended,
            ...availableActions.filter(
              (action) => action !== serverRecommended,
            ),
          ]
        : availableActions;
  const [choice, setChoice] = useState<ResolutionChoice | null>(
    orderedActions[0] ?? null,
  );
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [amountUnit, setAmountUnit] = useState<BalanceAdjustmentUnit>(
    "days",
  );
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const differenceValue =
    selectedCheck?.difference ?? selectedCheck?.rawDifference;
  const enteredAdjustment = normalizeBalanceAdjustmentAmount(amount);
  const canShowPreview =
    choice === "adjustment" && enteredAdjustment !== null;
  const { preview, loading: previewLoading, error: previewError } = useBalanceAdjustmentPreview({
    requestKey: canShowPreview ? JSON.stringify([
      employee.id, employee.userId, employee.revision, employee.recalculationRequired, employee.timesheetVersionId,
      employee.payslipRecordId, employee.hoursPerDay, selectedCheck,
      monthLabel, monthClosed, timesheetLocked, displayUnit,
      selectedKey, enteredAdjustment, amountUnit,
    ]) : null,
    checkKey: selectedKey,
    amount: enteredAdjustment,
    unit: amountUnit,
    onPreview: onPreviewAdjust,
  });
  const adjustmentResult =
    preview &&
    preview.inputAmount != null
      ? `${formatValueInUnit(
          preview.afterEffectiveBalanceHours,
          displayUnit,
          employee.hoursPerDay,
        )} after`
      : canShowPreview
        ? "Calculating preview…"
        : "Enter an amount";
  const adjustmentUnits: readonly BalanceAdjustmentUnit[] =
    balanceAdjustmentUnitOptions;
  const standardEvidenceItems = [
    selectedCheck?.timesheetValue != null
      ? {
          label: "Timesheet",
          value: formatValueInUnit(
            selectedCheck.timesheetValue,
            displayUnit,
            employee.hoursPerDay,
          ),
          tone: "text-foreground",
        }
      : null,
    selectedCheck?.payslipValue != null
      ? {
          label: "Payslip",
          value: formatValueInUnit(
            selectedCheck.payslipValue,
            displayUnit,
            employee.hoursPerDay,
          ),
          tone: "text-foreground",
        }
      : null,
    differenceValue != null
      ? {
          label: "Difference",
          value: formatValueInUnit(
            differenceValue,
            displayUnit,
            employee.hoursPerDay,
          ),
          tone: "text-danger",
        }
      : null,
    choice === "adjustment" ||
    Math.abs(selectedCheck?.appliedAdjustment ?? 0) > 0
      ? {
          label: "Adjustment",
          value:
            choice === "adjustment"
              ? canShowPreview
                ? adjustmentResult
                : "Enter amount"
              : formatValueInUnit(
                  selectedCheck?.appliedAdjustment,
                  displayUnit,
                  employee.hoursPerDay,
                ),
          tone: "text-warning",
        }
      : null,
  ].filter(
    (
      item,
    ): item is {
      label: string;
      value: string;
      tone: string;
    } => item !== null,
  );
  const coveredHours = selectedCheck?.timesheetValue;
  const includedAbsenceHours =
    selectedKey === "workload" &&
    coveredHours != null &&
    employee.payslipPaidHours != null
      ? coveredHours - employee.payslipPaidHours
      : null;
  const evidenceItems =
    selectedKey === "workload"
      ? [
          {
            label: "Payslip worked",
            value: formatValueInUnit(
              employee.payslipPaidHours,
              displayUnit,
              employee.hoursPerDay,
            ),
            tone: "text-foreground",
          },
          {
            label: "Included leave & absence",
            value: formatValueInUnit(
              includedAbsenceHours,
              displayUnit,
              employee.hoursPerDay,
            ),
            tone: "text-foreground",
          },
          {
            label: "Covered hours",
            value: formatValueInUnit(
              coveredHours,
              displayUnit,
              employee.hoursPerDay,
            ),
            tone: "text-foreground",
          },
          {
            label: "Contract hours",
            value: formatValueInUnit(
              employee.payslipNominalHours ?? selectedCheck?.payslipValue,
              displayUnit,
              employee.hoursPerDay,
            ),
            tone: "text-foreground",
          },
          ...(differenceValue != null
            ? [
                {
                  label: "Difference",
                  value: formatValueInUnit(
                    differenceValue,
                    displayUnit,
                    employee.hoursPerDay,
                  ),
                  tone: "text-danger",
                },
              ]
            : []),
        ]
      : standardEvidenceItems;
  const resultSummary = (() => {
    switch (choice) {
      case "inspect_payslip":
        return "Open the confirmed payslip and verify its extracted values.";
      case "correct_extracted_worked":
        return "Correct PayDay's extracted worked hours without changing the source PDF.";
      case "correct_timesheet":
        return "Correct the timesheet, then check this result again.";
      case "adjustment":
        return enteredAdjustment !== null
          ? `Apply an audited ${enteredAdjustment.toFixed(2)} ${amountUnit === "hours" ? "hours" : "days"} adjustment.`
          : "Enter the audited adjustment to apply.";
      case "accept_exception":
        return "Keep the current values and record an audited exception.";
      default:
        return "No resolution action is available for this check. Refresh the calculation before continuing.";
    }
  })();

  const selectIssue = (key: BalanceCheckKey) => {
    const nextCheck = reviewableChecks.find(
      (issue) => issue.key === key,
    )?.check;
    const nextOptions = balanceResolutionOptions(nextCheck?.resolution);
    setSelectedKey(key);
    setChoice(
      key === "workload"
        ? "inspect_payslip"
        : nextOptions.recommendedAction ?? nextOptions.actions[0] ?? null,
    );
    setAmount("");
    setAmountUnit("days");



    setAcknowledged(false);
    setError(null);
  };

  const run = async (action: () => Promise<unknown>) => {
    setSubmitting(true);
    setError(null);
    try {
      await action();
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The resolution could not be saved.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reasonValid = isBalanceAdjustmentReasonValid(reason);
  const canSubmit =
    choice !== null &&
    isResolutionAction(choice) &&
    acknowledged &&
    reasonValid &&
    (choice !== "adjustment"
      ? true
      : enteredAdjustment !== null &&
        canShowPreview &&
        preview !== null &&
        !previewLoading);


  if (selectedCheck?.accepted === true) {
    const acceptedDifference =
      selectedCheck.difference ?? selectedCheck.rawDifference;
    const acceptedCoveredHours = selectedCheck.timesheetValue;
    const acceptedAbsenceHours =
      selectedKey === "workload" &&
      acceptedCoveredHours != null &&
      employee.payslipPaidHours != null
        ? acceptedCoveredHours - employee.payslipPaidHours
        : null;
    const acceptedEvidence =
      selectedKey === "workload"
        ? [
            ["Payslip worked", employee.payslipPaidHours],
            ["Included leave & absence", acceptedAbsenceHours],
            ["Covered hours", acceptedCoveredHours],
            [
              "Contract hours",
              employee.payslipNominalHours ?? selectedCheck.payslipValue,
            ],
            ["Difference", acceptedDifference],
          ]
        : [
            ["Timesheet", selectedCheck.timesheetValue],
            ["Payslip", selectedCheck.payslipValue],
            ["Difference", acceptedDifference],
          ];
    return (
      <Modal
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Accepted ${selectedKey === "vacation" ? "vacation" : checkLabels[selectedKey].toLowerCase()} evidence`}
        description={`${employee.employeeName} · ${monthLabel} · Read-only audit evidence`}
        size="md"
        closeLabel="Close accepted comparison evidence"
        className="balance-resolution-modal balance-evidence-inspector"
      >
        <div className="grid gap-4 py-1">
          <dl className="grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-3">
            {acceptedEvidence.map(([label, value]) => (
              <div key={String(label)} className="bg-background px-3 py-3">
                <dt className="text-xs font-medium text-foreground-muted">
                  {label}
                </dt>
                <dd className="mt-1 text-sm font-semibold tabular-nums text-foreground">
                  {formatValueInUnit(
                    value as number | null | undefined,
                    displayUnit,
                    employee.hoursPerDay,
                  )}
                </dd>
              </div>
            ))}
          </dl>
          <div className="rounded-xl border border-border bg-background px-4 py-3">
            <p className="text-sm font-semibold text-foreground">
              {selectedCheck.acceptedBy || "Payroll reviewer"}
            </p>
            <p className="mt-1 text-xs text-foreground-muted">
              {selectedCheck.acceptedAt
                ? new Date(selectedCheck.acceptedAt).toLocaleString()
                : "Acceptance time unavailable"}
            </p>
            <p className="mt-3 text-sm leading-5 text-foreground">
              {selectedCheck.acceptanceReason || "No acceptance reason recorded."}
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Resolve ${employee.employeeName}`}
      description={`${monthLabel} · ${checkLabels[selectedKey]} · Unresolved`}
      size="xl"
      closeLabel={`Close ${employee.employeeName} balance resolution`}
      className="balance-resolution-modal"
    >
      <div className="grid gap-5 py-1">
        {issues.length > 1 && (
          <div className="grid gap-2" aria-label="Unresolved checks">
            <p className="text-xs leading-5 text-foreground-muted">
              These are separate payroll fields. Review and record a decision
              for each one; resolving one does not change the others.
            </p>
            <div className="flex flex-wrap gap-2">
              {issues.map(({ key }) => (
                <Button variant="ghost"
                  key={key}
                  type="button"
                  onClick={() => selectIssue(key)}
                  aria-pressed={selectedKey === key}
                  className={`min-h-11 min-w-11 max-w-full whitespace-normal rounded-lg border px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selectedKey === key ? "border-danger bg-danger-subtle text-danger" : "border-border text-foreground-muted hover:bg-elevated"}`}
                >
                  {checkLabels[key]}
                </Button>
              ))}
            </div>
          </div>
        )}

        <section
          aria-labelledby="balance-source-comparison"
          className="overflow-hidden rounded-xl border border-border bg-background"
        >
          <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
            <h3
              id="balance-source-comparison"
              className="text-sm font-semibold text-foreground"
            >
              Source comparison
            </h3>
            <span className="text-xs font-medium text-foreground-muted">
              {checkLabels[selectedKey]}
            </span>
          </div>
          <dl className="flex flex-wrap divide-x divide-border">
            {evidenceItems.map((item) => (
              <div
                key={item.label}
                className="flex min-w-0 basis-36 flex-1 flex-wrap items-baseline justify-between gap-3 px-3 py-2"
              >
                <dt className="text-xs font-medium text-foreground-subtle">
                  {item.label}
                </dt>
                <dd
                  className={`whitespace-nowrap text-sm font-semibold tabular-nums ${item.tone}`}
                >
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border px-3 py-2"
            title={selectedCheck?.message}
          >
            <span className="text-xs font-medium text-foreground-subtle">
              Result
            </span>
            <strong className="text-sm font-semibold text-foreground">
              {resultSummary}
            </strong>
          </div>
        </section>

        <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(15rem,.7fr)_minmax(0,1.3fr)]">
          <fieldset className="grid min-w-0 content-start gap-2">
            <legend className="mb-1 text-sm font-semibold text-foreground">
              Choose a resolution
            </legend>
            {orderedActions.length === 0 && (
              <p
                role="status"
                className="rounded-lg border border-warning/35 bg-warning-subtle px-3 py-3 text-sm text-warning"
              >
                The server did not make a resolution action available for this
                check. Close this dialog, refresh the calculation, and review
                the returned actions.
              </p>
            )}
            {orderedActions.map((action, index) => (
              <label
                key={action}
                className={`flex min-h-12 cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 ${choice === action ? "border-accent bg-accent-subtle" : "border-border hover:bg-elevated"}`}
              >
                <input
                  type="radio"
                  name="resolution"
                  value={action}
                  checked={choice === action}
                  onChange={() => {
                    setChoice(action);
                    setAcknowledged(false);
                  }}
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground">
                    {BALANCE_RESOLUTION_PRESENTATION[action].label}
                  </span>
                  {index === 0 && recommended === action && (
                    <span className="mt-0.5 block text-xs text-accent">
                      Recommended
                    </span>
                  )}
                  <span className="mt-1 block text-xs leading-4 text-foreground-muted">
                    {BALANCE_RESOLUTION_PRESENTATION[action].summary}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          <section className="min-w-0 rounded-xl border border-border bg-background p-4">
            <div className="flex items-start gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-danger-subtle text-danger">
                <AlertTriangle size={18} aria-hidden="true" />
              </span>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Next step
                </h3>
                <p className="mt-1 text-sm leading-5 text-foreground-muted">
                  {resultSummary}
                </p>
              </div>
            </div>

            {choice === "inspect_payslip" && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className={modalActionButtonClass}
                  onClick={onOpenPayslip}
                  disabled={!employee.payslipRecordId || !onOpenPayslip}
                >
                  <Eye size={16} aria-hidden="true" /> Open payslip &amp; values
                </Button>
              </div>
            )}

            {choice === "correct_extracted_worked" && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="lg"
                  variant="secondary"
                  className={modalActionButtonClass}
                  onClick={onCorrectPayslipWorked}
                  disabled={!onCorrectPayslipWorked}
                >
                  <PencilLine size={16} aria-hidden="true" /> Correct extracted
                  worked hours
                </Button>
              </div>
            )}

            {choice === "correct_timesheet" && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className={modalActionButtonClass}
                  onClick={() => void onDownloadTimesheet()}
                  disabled={!employee.timesheetVersionId}
                >
                  <Download size={16} aria-hidden="true" /> Download timesheet
                </Button>
                {monthClosed ? (
                  <Button
                    type="button"
                    size="lg"
                    variant="warning"
                    className={modalActionButtonClass}
                    onClick={() => void run(() => onReopen(reason.trim()))}
                    disabled={!reasonValid || submitting}
                  >
                    <RotateCcw size={16} aria-hidden="true" /> Reopen month
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="lg"
                    variant="secondary"
                    className={modalActionButtonClass}
                    onClick={() =>
                      void onToggleTimesheet(
                        timesheetLocked ? "unlock" : "lock",
                      )
                    }
                    disabled={busy}
                  >
                    {timesheetLocked ? (
                      <LockKeyholeOpen size={16} aria-hidden="true" />
                    ) : (
                      <LockKeyhole size={16} aria-hidden="true" />
                    )}
                    {timesheetLocked ? "Unlock timesheet" : "Lock timesheet"}
                  </Button>
                )}
              </div>
            )}

            {choice === "adjustment" && (
              <div className="mt-4 grid gap-3">
                <label className="grid max-w-xs gap-1.5 text-sm font-medium text-foreground">
                  Adjustment amount
                  <input
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    inputMode="decimal"
                    placeholder="e.g. 1.31"
                    aria-label={`Adjustment in ${unitLabel(amountUnit)}`}
                    className="h-11 rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                </label>
                <label className="grid max-w-xs gap-1.5 text-sm font-medium text-foreground">
                  Input unit
                  <select
                    value={amountUnit}
                    onChange={(event) =>
                      setAmountUnit(event.target.value as BalanceAdjustmentUnit)
                    }
                    className="h-11 rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                  >
                    {adjustmentUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unitLabel(unit)}
                      </option>
                    ))}
                  </select>
                </label>
                <div
                  className={`rounded-lg border px-3 py-3 ${previewLoading ? "border-warning/40 bg-warning-subtle/40" : preview ? "border-success/40 bg-success-subtle/30" : "border-border"}`}
                >
                  <p className="text-sm font-medium text-foreground">
                    {previewLoading
                      ? "Calculating balance preview…"
                      : preview
                        ? `After applying this adjustment: ${formatValueInUnit(preview.afterEffectiveBalanceHours, displayUnit, employee.hoursPerDay)}`
                        : canShowPreview
                          ? "Waiting for preview…"
                          : "Enter an adjustment amount to preview the result."}
                  </p>
                  {previewError && (
                    <p role="alert" className="mt-1 text-xs text-danger">
                      {previewError}
                    </p>
                  )}
                  {preview ? (
                    <p className="mt-1 break-all text-xs text-foreground-subtle">
                      Conversion basis: {preview.conversionBasis}
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            {(choice === "adjustment" ||
              choice === "accept_exception" ||
              (choice === "correct_timesheet" && monthClosed)) && (
              <>
                <label className="mt-4 grid gap-1.5 text-sm font-medium text-foreground">
                  Audit reason
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Briefly describe what you verified"
                    className="rounded-lg border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                  <span className="text-xs font-normal text-foreground-subtle">
                    At least 8 characters
                  </span>
                </label>
                {choice !== "correct_timesheet" && (
                  <label className="mt-4 flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface px-3 py-3">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(event) =>
                        setAcknowledged(event.target.checked)
                      }
                      className="mt-0.5"
                    />
                    <span className="text-sm leading-5 text-foreground">
                      <strong>
                        {actorName}, are you sure you want to approve this
                        change?
                      </strong>
                      <span className="mt-1 block text-foreground-muted">
                        Yes, apply this resolution and record it in the payroll
                        audit.
                      </span>
                    </span>
                  </label>
                )}
              </>
            )}

            {error && (
              <p role="alert" className="mt-3 text-sm text-danger">
                {error}
              </p>
            )}

            {(choice === "adjustment" ||
              choice === "accept_exception") && (
              <div className="mt-4 flex justify-end">
                <Button
                  type="button"
                  size="lg"
                  className={modalActionButtonClass}
                  disabled={!canSubmit || submitting}
                  onClick={() =>
                    void run(() => {
                      if (choice === "adjustment") {
                        if (!preview) {
                          throw new Error("Adjustment preview is required.");
                        }
                        return onAdjust(
                          selectedKey,
                          enteredAdjustment,
                          amountUnit,
                          reason.trim(),
                          preview,
                        );
                      }
                      return onAccept(selectedKey, choice, reason.trim());
                    })
                  }
                >
                  {choice === "adjustment" ? (
                    <SlidersHorizontal size={16} aria-hidden="true" />
                  ) : choice === "accept_exception" ? (
                    <ShieldCheck size={16} aria-hidden="true" />
                  ) : (
                    <CheckCircle2 size={16} aria-hidden="true" />
                  )}
                  {submitting
                    ? "Saving…"
                    : BALANCE_RESOLUTION_PRESENTATION[choice].label}
                </Button>
              </div>
            )}
          </section>
        </div>
      </div>
    </Modal>
  );
}
